import { ListPlus, Store as StoreIcon } from "lucide-react";
import { de, type ShopKey } from "@ainkauf/core";

/**
 * "Keine Liste für den Baumarkt."
 *
 * Erscheint genau dann, wenn ein Artikel eindeutig woandershin gehört, es
 * dafür aber keine Liste gibt. Daniel am 03.10.2026: "Wenn du der Meinung
 * bist, dass es hier noch nicht die richtige Liste für diese Art von Item
 * gibt, dann könntest du mich fragen, ob du diese Liste anlegen sollst oder
 * ob das einfach in Supermarkt gepackt werden soll."
 *
 * Zwei Knöpfe, keine Eingabe: Der Name der Liste steht fest, sonst wäre es
 * ein Formular mitten im Einkaufen. Umbenennen geht später in den
 * Einstellungen.
 *
 * Der Artikel ist zu diesem Zeitpunkt **noch nicht angelegt**. Er entsteht
 * erst mit der Antwort, damit er nicht erst in der falschen Liste auftaucht
 * und dann springt.
 */
export function ShopSheet({
  name,
  shop,
  standardliste,
  onCreate,
  onKeep,
}: {
  name: string;
  shop: ShopKey;
  /** Name der Standardliste, für den zweiten Knopf. */
  standardliste: string;
  onCreate: () => void;
  onKeep: () => void;
}) {
  const laden = de.shops.namen[shop];

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" role="presentation">
      <div
        role="dialog"
        aria-label={de.shops.missingTitle(laden)}
        data-testid="shop-sheet"
        data-shop={shop}
        className="flex flex-col gap-4 rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <div>
          <h2 className="text-xl">{de.shops.missingTitle(laden)}</h2>
          <p className="mt-1 text-sm text-ink-2">{de.shops.missingHint(name, laden)}</p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onCreate}
            data-testid="shop-create"
            className="flex min-h-[52px] items-center gap-3 rounded-[var(--radius-lg)] bg-accent px-4 font-display text-base font-extrabold text-white"
          >
            <ListPlus size={20} strokeWidth={2} aria-hidden />
            {de.shops.createList(laden)}
          </button>
          <button
            type="button"
            onClick={onKeep}
            data-testid="shop-keep"
            className="flex min-h-[52px] items-center gap-3 rounded-[var(--radius-lg)] bg-paper-2 px-4 font-semibold text-ink-2"
          >
            <StoreIcon size={20} strokeWidth={1.5} aria-hidden />
            {de.shops.keepDefault(standardliste)}
          </button>
        </div>
      </div>
    </div>
  );
}
